import { WorksheetAssociatedTestSection } from 'Components/Worksheets/Core';
import { AssociatedTestType } from 'Components/Worksheets/Core/WorksheetAssociatedTestSection/types';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { ISieveSize } from 'Data/ViewModels/Equipment/ISieveSize';
import { hasValue, valueOrDefault } from 'Lib/Any';
import { FormatTypes } from 'Lib/Format';
import { Guid } from 'Lib/Guid';
import { pipe } from 'Lib/Hooks/Selectors';
import React from 'react';
import { useSelector } from 'react-redux';
import { Checkbox, Field, NumericInput, RadioGroup, TextOutput } from '../../../../../Core';
import { withInjectedProps } from '../../../../../Core/InjectedPropsContext';
import { ListFieldSection } from '../../../../../Layout';
import externalTestMethods from '../../../../ExternalTestMethods';
import { default as QestIds, default as qestIds } from '../../../../QestIds';

let AsFieldMddSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const importedFieldMDDID = useSelector(createWorksheetSelector!('AssociatedCompactionData.ImportedTest.Id')) as Guid;
  const sieveSize = useSelector(createWorksheetSelector!('OversizeSieveSize')) as ISieveSize;
  const stabiliserUsed = useSelector(
    pipe(createWorksheetSelector!('StabiliserUsed'), valueOrDefault(false))
  ) as boolean;
  const hasAdditionOfStabiliserTime = useSelector(
    pipe(createWorksheetSelector!('TimeBetweenAdditionOfStabiliserAndCompaction'), hasValue)
  );
  const externalStabiliserUsed = useSelector(
    pipe(
      createWorksheetSelector!('AssociatedCompactionData.ExternalTest.Results.StabiliserUsed'),
      valueOrDefault(false)
    )
  ) as boolean;
  const ImportedFromAmddTest = useSelector(
    pipe(
      createWorksheetSelector!('AssociatedCompactionData.ImportedTest.Type.Id'),
      (t) => t === qestIds.assignedMaximumDryDensity.AS_1289_5_4_2.assignedValueTest
    )
  );
  const allowAMDDOnly = useSelector(pipe(createWorksheetSelector!('AllowAMDDOnly'), valueOrDefault(false))) as boolean;

  return (
    <>
      <WorksheetAssociatedTestSection
        heading="Preparation and Compaction Data"
        name="AssociatedCompactionData"
        testType="Maximum Dry Density"
        defaultSelection={AssociatedTestType.AutoImported}
        associatedTestModel={{
          autoImported: true,
          imported: {
            testMethodIds: allowAMDDOnly
              ? [QestIds.assignedMaximumDryDensity.AS_1289_5_4_2.assignedValueTest]
              : [
                  QestIds.assignedMaximumDryDensity.AS_1289_5_4_2.assignedValueTest,
                  QestIds.maximumDryDensity.AS_1289_5_1_1.rev2017,
                  QestIds.maximumDryDensity.AS_1289_5_2_1.rev2017,
                ],
            importPropertyName: 'AssociatedCompactionData',
            importedTestResults: (
              <ListFieldSection>
                <Field
                  input={<TextOutput />}
                  name="StabiliserUsed"
                  format={{ type: FormatTypes.YesNo }}
                  visible={!ImportedFromAmddTest}
                />
                <Field
                  input={<TextOutput />}
                  visible={!ImportedFromAmddTest && stabiliserUsed && !hasAdditionOfStabiliserTime}
                  name="TimesBetweenAdditionOfStabiliserAndCompaction"
                />
                <Field
                  input={<TextOutput />}
                  visible={!ImportedFromAmddTest && stabiliserUsed && hasAdditionOfStabiliserTime}
                  name="TimeBetweenAdditionOfStabiliserAndCompaction"
                />
                <Field input={<TextOutput />} name="CompactiveEffort" />
                <Field input={<TextOutput />} name="TareMass" visible={!ImportedFromAmddTest} />
                <Field input={<TextOutput />} name="TotalWetSampleMass" visible={!ImportedFromAmddTest} />
                <Field input={<TextOutput />} name="OversizeSieveSize" value={sieveSize?.Caption} />
                <Field input={<TextOutput />} name="OversizeMaterialWetMass" visible={!ImportedFromAmddTest} />
                <Field
                  input={<TextOutput />}
                  name="AssignedDate"
                  visible={ImportedFromAmddTest}
                  format={{ type: FormatTypes.Date }}
                />
                <Field input={<TextOutput />} name="MaximumDryDensity" />
                <Field input={<TextOutput />} name="OptimumMoistureContent" />
              </ListFieldSection>
            ),
            importModel: {
              Id: importedFieldMDDID,
            },
          },
          external: {
            testMethods: [
              externalTestMethods.maximumDryDensity.AS_1289_5_1_1,
              externalTestMethods.maximumDryDensity.AS_1289_5_2_1,
            ],
            permitOtherTestMethods: false,
            externalTestResults: (
              <ListFieldSection>
                <Field input={<Checkbox />} name="AssociatedCompactionData.ExternalTest.Results.StabiliserUsed" />
                <Field
                  input={<NumericInput />}
                  visible={externalStabiliserUsed}
                  name="AssociatedCompactionData.ExternalTest.Results.TimeBetweenAdditionOfStabiliserAndCompaction"
                />
                <Field input={<TextOutput />} name="CompactiveEffort" />
                <Field
                  input={<NumericInput />}
                  name="AssociatedCompactionData.ExternalTest.Results.MaximumDryDensity"
                />
                <Field
                  input={<NumericInput />}
                  name="AssociatedCompactionData.ExternalTest.Results.OptimumMoistureContent"
                />
                <Field
                  input={<RadioGroup />}
                  name="AssociatedCompactionData.ExternalTest.Results.OversizeSieveApertureSizeId"
                  options={[
                    { label: '19.0mm', value: '19_0' },
                    { label: '37.5mm', value: '37_5' },
                  ]}
                />
              </ListFieldSection>
            ),
          },
        }}
      />
    </>
  );
};

AsFieldMddSection = withInjectedProps(AsFieldMddSection);

export { AsFieldMddSection };
export default AsFieldMddSection;
